/**
 * Vardiya Kontrol Servisi — Faz 5.4
 *
 * Yayınlanmış vardiyaları gerçek giriş-çıkış kayıtlarıyla eşler.
 *
 * KURALLAR (spec madde 6 + kullanıcı kararı):
 *  Geç gelme:
 *    0-15 dk: tolere
 *    15+ dk: kayıt edilir (bitiş kayar → eksik mesai yok)
 *    Aylık 3+ kez 15+ dk geç: 3'üncüden itibaren ekstra geç başına BOSS otomatik puantaj cezası
 *  Erken çıkma:
 *    0-15 dk: tolere
 *    15+ dk: eksik dakika hesabı → aylık personel için maaştan kesilir
 *  No-show (vardiyaya gelmedi):
 *    Aylık personel: tüm vardiya saati maaştan kesilir
 *    Saatlik personel: zaten kayıt yok → ödeme yok (doğal)
 *    Her iki tip için: BOSS otomatik puantaj cezası
 *
 * Hesap "compute-only": gerçek puantaj kaydı oluşturulmaz, sadece maaşa yansır.
 */

import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import type { Vardiya } from './vardiya-service'
import type { Kayit } from './kayit-service'

const TOLERANS_DK = 15
const PENCERE_DK = 60      // vardiya öncesi/sonrası ne kadarlık kayıtlar dikkate alınır

/** Otomatik ceza tutarları (sabit) */
export const GEC_GELME_CEZASI_TL = 100   // 3'üncüden itibaren her ekstra için
export const NO_SHOW_CEZASI_TL = 300     // vardiya başına

export type VardiyaKontrolDurumu =
  | 'normal'
  | 'gec_geldi'
  | 'erken_cikti'
  | 'gec_geldi_erken_cikti'
  | 'no_show'
  | 'devam_ediyor'   // vardiya henüz bitmedi
  | 'izinli'
  | 'iptal'

export interface VardiyaKontrolSonuc {
  vardiya: Vardiya
  baslangicZamani: Date
  bitisZamani: Date
  vardiyaSureDk: number
  gercekGirisZamani?: Date
  gercekCikisZamani?: Date
  gecmeDk: number         // 0 veya 15+ (TOLERANS_DK altı 0)
  erkenCikmaDk: number    // 0 veya 15+
  eksikDakika: number     // maaştan kesilecek dk (erken çıkma + no_show vardiya)
  durum: VardiyaKontrolDurumu
}

/** Vardiya başlangıcı → Date */
function vardiyaBaslangicZamani(v: Vardiya): Date {
  const [h, m] = v.baslangic.split(':').map(Number)
  const [yy, mm, dd] = v.tarih.split('-').map(Number)
  return new Date(yy!, mm! - 1, dd!, h!, m!, 0, 0)
}

/** Vardiya bitişi → Date (gece vardiyası ise ertesi gün) */
function vardiyaBitisZamani(v: Vardiya): Date {
  const [bsH, bsM] = v.baslangic.split(':').map(Number)
  const [btH, btM] = v.bitis.split(':').map(Number)
  const [yy, mm, dd] = v.tarih.split('-').map(Number)
  const d = new Date(yy!, mm! - 1, dd!, btH!, btM!, 0, 0)
  // Gece vardiyası: bitiş başlangıçtan küçük veya eşitse ertesi gün
  if (btH! < bsH! || (btH! === bsH! && btM! <= bsM!)) {
    d.setDate(d.getDate() + 1)
  }
  return d
}

/** Tek bir vardiyayı kayıtlarla eşleştirip sonuç döndür */
export function vardiyaKontrolEt(
  vardiya: Vardiya,
  tumKayitlar: Kayit[]
): VardiyaKontrolSonuc {
  const baslangic = vardiyaBaslangicZamani(vardiya)
  const bitis = vardiyaBitisZamani(vardiya)
  const sureDk = Math.floor((bitis.getTime() - baslangic.getTime()) / 60000)

  const ortak = {
    vardiya,
    baslangicZamani: baslangic,
    bitisZamani: bitis,
    vardiyaSureDk: sureDk,
    gecmeDk: 0,
    erkenCikmaDk: 0,
    eksikDakika: 0,
  }

  // İzinli veya iptal vardiya → hesaba dahil etme
  if (vardiya.durum === 'izinli') {
    return { ...ortak, durum: 'izinli' }
  }
  if (vardiya.durum === 'iptal') {
    return { ...ortak, durum: 'iptal' }
  }

  // Henüz bitmediyse → kontrol yapma
  if (Date.now() < bitis.getTime()) {
    return { ...ortak, durum: 'devam_ediyor' }
  }

  // Yayınlanmamış vardiya → kontrol yapma (geçmiş olsa bile)
  if (!vardiya.yayinlandi) {
    return { ...ortak, durum: 'devam_ediyor' }
  }

  // Pencere aralığındaki kayıtları al
  const pencereBas = baslangic.getTime() - PENCERE_DK * 60000
  const pencereBit = bitis.getTime() + PENCERE_DK * 60000
  const ilgiliKayitlar = tumKayitlar
    .filter((k) => {
      if (k.personelId !== vardiya.personelId) return false
      const t = k.zaman.toMillis()
      return t >= pencereBas && t <= pencereBit
    })
    .sort((a, b) => a.zaman.toMillis() - b.zaman.toMillis())

  const ilkGiris = ilgiliKayitlar.find((k) => k.tip === 'giris')

  // No-show: hiç giriş yok
  if (!ilkGiris) {
    return {
      ...ortak,
      eksikDakika: sureDk,
      durum: 'no_show',
    }
  }

  // Giriş varsa, son çıkışı bul (geriden ileri)
  const sonCikis = [...ilgiliKayitlar].reverse().find((k) => k.tip === 'cikis')

  const gercekGiris = ilkGiris.zaman.toDate()
  const gercekCikis = sonCikis?.zaman.toDate()

  // Geç gelme (giriş > planlı başlangıç)
  const gecmeRawDk = Math.max(0, Math.floor(
    (gercekGiris.getTime() - baslangic.getTime()) / 60000
  ))
  const gecmeKayit = gecmeRawDk > TOLERANS_DK ? gecmeRawDk : 0

  // Erken çıkma (çıkış < planlı bitiş)
  let erkenRawDk = 0
  if (gercekCikis) {
    erkenRawDk = Math.max(0, Math.floor(
      (bitis.getTime() - gercekCikis.getTime()) / 60000
    ))
  }
  const erkenKayit = erkenRawDk > TOLERANS_DK ? erkenRawDk : 0

  // Eksik dakika: sadece erken çıkma (geç gelmede bitiş kayar)
  const eksikDk = erkenKayit

  let durum: VardiyaKontrolDurumu = 'normal'
  if (gecmeKayit > 0 && erkenKayit > 0) durum = 'gec_geldi_erken_cikti'
  else if (gecmeKayit > 0) durum = 'gec_geldi'
  else if (erkenKayit > 0) durum = 'erken_cikti'

  return {
    ...ortak,
    gercekGirisZamani: gercekGiris,
    gercekCikisZamani: gercekCikis,
    gecmeDk: gecmeKayit,
    erkenCikmaDk: erkenKayit,
    eksikDakika: eksikDk,
    durum,
  }
}

/** O dönemin yayınlanmış vardiyalarını çek */
async function donemVardiyalari(personelId: string, donem: string): Promise<Vardiya[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, 'vardiyalar'),
    where('personelId', '==', personelId)
  )
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Vardiya)
  const [y, m] = donem.split('-')
  const ayPrefix = `${y}-${m}-`
  return liste.filter((v) => v.tarih.startsWith(ayPrefix) && v.yayinlandi)
}

/** O dönemin tüm kayıtlarını çek (penceresel sınırlamayla) */
async function donemKayitlari(personelId: string, donem: string): Promise<Kayit[]> {
  const db = getFirestoreDb()
  const [y, m] = donem.split('-').map(Number)
  // Pencere için bir önceki ay sonu + ertesi ay başı dahil
  const baslangic = new Date(y!, m! - 1, 1, 0, 0, 0, 0)
  baslangic.setDate(baslangic.getDate() - 2)
  const bitis = new Date(y!, m!, 1, 0, 0, 0, 0)
  bitis.setDate(bitis.getDate() + 2)
  const q = query(
    collection(db, 'kayitlar'),
    where('personelId', '==', personelId)
  )
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
  return liste.filter((k) => {
    const t = k.zaman.toMillis()
    return t >= Timestamp.fromDate(baslangic).toMillis() &&
           t < Timestamp.fromDate(bitis).toMillis()
  })
}

export interface VardiyaKontrolOzeti {
  sonuclar: VardiyaKontrolSonuc[]
  gecGelmeSayisi: number    // 15+ dk geç gelme
  erkenCikmaSayisi: number  // 15+ dk erken çıkma
  noShowSayisi: number
  toplamEksikDk: number     // erken çıkma + no-show toplam
}

export async function ayVardiyaKontrolu(
  personelId: string,
  donem: string
): Promise<VardiyaKontrolOzeti> {
  const [vardiyalar, kayitlar] = await Promise.all([
    donemVardiyalari(personelId, donem),
    donemKayitlari(personelId, donem),
  ])

  const sonuclar = vardiyalar.map((v) => vardiyaKontrolEt(v, kayitlar))

  let gecGelmeSayisi = 0
  let erkenCikmaSayisi = 0
  let noShowSayisi = 0
  let toplamEksikDk = 0
  for (const s of sonuclar) {
    if (s.gecmeDk > 0) gecGelmeSayisi++
    if (s.erkenCikmaDk > 0) erkenCikmaSayisi++
    if (s.durum === 'no_show') noShowSayisi++
    toplamEksikDk += s.eksikDakika
  }

  return { sonuclar, gecGelmeSayisi, erkenCikmaSayisi, noShowSayisi, toplamEksikDk }
}
